import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
  Box,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Tooltip,
} from "@mui/material";
import BrandBuyButton from "./BrandBuyButton";
import {
  getAffiliateKey,
  getExternalUrl,
  logLinkClick,
} from "../../utils/links";
import momenttz from "moment-timezone";
import {
  getAccent,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../../constants/colors";

const getHeaderCellSx = (darkMode) => ({
  paddingTop: "6px",
  paddingBottom: "6px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: getMutedText(darkMode),
});

const VIEW_BUTTON_SX = {
  borderRadius: "9px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const getUrl = (url) => {
  const rawUrl = new URL(url);
  return rawUrl.hostname.split(/\./).slice(-2).join(".");
};

const getMostRecentPriceUpdate = (retailers) => {
  if (!retailers || !retailers.length) return null;
  let mostRecentDate = 0;
  retailers.forEach(({ lastUpdated }) => {
    if (new Date(lastUpdated) > new Date(mostRecentDate)) {
      mostRecentDate = lastUpdated;
    }
  });
  return mostRecentDate;
};

const ItemRetailerModule = ({
  item,
  darkMode,
  gearText,
  dynamicWidth,
  packwizardUrl,
}) => {
  const mostRecentDate = getMostRecentPriceUpdate(item.retailers);
  if (!item) return <></>;
  return (
    <Paper
      elevation={0}
      sx={{
        width: dynamicWidth,
        borderRadius: "12px",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundImage: "none",
        overflow: "hidden",
      }}
    >
      {!item.retailers || item.retailers.length === 0 ? (
        <Container
          sx={{
            width: dynamicWidth,
            padding: "0px 0px 0px 0px",
          }}
        >
          {getAffiliateKey(item.url) ? (
            <>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ backgroundImage: "none" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        sx={getHeaderCellSx(darkMode)}
                      >
                        Retailer
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={getHeaderCellSx(darkMode)}
                      >
                        Price
                      </TableCell>
                      <TableCell
                        sx={getHeaderCellSx(darkMode)}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center">
                        <BrandBuyButton
                          retailer={getAffiliateKey(item.url)}
                          url={item.url}
                          darkMode={darkMode}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "15px",
                          fontFamily: HEADING_FONT,
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        <Tooltip title="This price may be out of date as we do not capture live pricing data for this item for this retailer">
                          <Typography>
                            {Number(item.price) ? (
                              <Link
                                target="_blank"
                                href={getExternalUrl(item.url)}
                                onClick={() => logLinkClick(item.url)}
                                sx={{
                                  textDecoration: "none",
                                  color: "orange",
                                  "&:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                              >
                                {Number(item.price).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                })}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Link
                          target="_blank"
                          href={getExternalUrl(item.url)}
                          onClick={() => logLinkClick(item.url)}
                        >
                          <Button
                            variant="contained"
                            disableElevation
                            size="medium"
                            color="success"
                            sx={VIEW_BUTTON_SX}
                          >
                            View
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper
              elevation={0}
              sx={{
                textAlign: "center",
                height: "300px",
                borderRadius: "12px",
                backgroundImage: "none",
              }}
              component={Stack}
              direction="column"
              justifyContent="center"
            >
              <Stack
                alignItems="center"
                sx={{ marginTop: "-20px" }}
                spacing={2}
              >
                <Typography
                  sx={{
                    fontSize: "17px",
                    fontFamily: HEADING_FONT,
                    color: getMutedText(darkMode),
                    maxWidth: "300px",
                  }}
                >{`We don't yet offer price tracking for this ${
                  gearText ? gearText : "item"
                }`}</Typography>
                <Link
                  target="_blank"
                  href={getExternalUrl(item.url)}
                  onClick={() => logLinkClick(item.url)}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    sx={{ ...VIEW_BUTTON_SX, width: dynamicWidth }}
                  >
                    View at {getUrl(item.url)}
                  </Button>
                </Link>
              </Stack>
            </Paper>
          )}
        </Container>
      ) : (
        <Container
          sx={{
            width: dynamicWidth,
            padding: "0px 0px 0px 0px",
          }}
        >
          <Stack spacing={0}>
            <Stack spacing={0} alignItems="flex-end">
              {item.retailers.length > 0 ? (
                <TableContainer
                  sx={{
                    marginTop: "8px",
                    backgroundImage: "none",
                  }}
                  component={Paper}
                  elevation={0}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={getHeaderCellSx(darkMode)}
                        >
                          Retailer
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={getHeaderCellSx(darkMode)}
                        >
                          Price
                        </TableCell>
                        <TableCell
                          sx={getHeaderCellSx(darkMode)}
                        ></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!getAffiliateKey(item.url) ||
                      item.retailers.find(
                        ({ retailer }) =>
                          retailer.toLowerCase() === getAffiliateKey(item.url)
                      ) ? (
                        <></>
                      ) : (
                        <TableRow>
                          <TableCell align="center">
                            <BrandBuyButton
                              retailer={getAffiliateKey(item.url)}
                              url={item.url}
                              darkMode={darkMode}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "15px",
                              fontFamily: HEADING_FONT,
                              fontWeight: 600,
                              fontVariantNumeric: "tabular-nums",
                              color: "orange",
                            }}
                          >
                            <Tooltip title="This price may be out of date as we do not capture live pricing data for this item for this retailer">
                              <Typography>
                                {Number(item.price) ? (
                                  <Link
                                    target="_blank"
                                    href={getExternalUrl(item.url)}
                                    onClick={() => logLinkClick(item.url)}
                                    sx={{
                                      textDecoration: "none",
                                      color: "orange",
                                      "&:hover": {
                                        cursor: "pointer",
                                      },
                                    }}
                                  >
                                    {Number(item.price).toLocaleString(
                                      "en-US",
                                      {
                                        style: "currency",
                                        currency: "USD",
                                      }
                                    )}
                                  </Link>
                                ) : (
                                  "-"
                                )}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Link
                              target="_blank"
                              href={getExternalUrl(item.url)}
                              onClick={() => logLinkClick(item.url)}
                            >
                              <Button
                                variant="contained"
                                disableElevation
                                size="medium"
                                color="success"
                                sx={VIEW_BUTTON_SX}
                              >
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      )}
                      {item.retailers.map(
                        ({ retailer, buyLink, salePrice }) => (
                          <TableRow key={`${retailer}-${buyLink}`}>
                            <TableCell align="center">
                              <BrandBuyButton
                                retailer={retailer}
                                url={buyLink}
                                darkMode={darkMode}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "15px",
                                fontFamily: HEADING_FONT,
                                fontWeight: 600,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {Number(salePrice) ? (
                                <Link
                                  target="_blank"
                                  href={getExternalUrl(buyLink, false, item)}
                                  onClick={() => logLinkClick(buyLink)}
                                  sx={{
                                    textDecoration: "none",
                                    "&:hover": {
                                      cursor: "pointer",
                                    },
                                  }}
                                >
                                  {Number(salePrice).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  })}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>
                              <Link
                                target="_blank"
                                href={getExternalUrl(buyLink, false, item)}
                                onClick={() => logLinkClick(buyLink)}
                              >
                                <Button
                                  variant="contained"
                                  disableElevation
                                  size="medium"
                                  color="success"
                                  sx={VIEW_BUTTON_SX}
                                >
                                  View
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  textAlign="center"
                  sx={{
                    marginTop: "10px",
                    fontFamily: HEADING_FONT,
                    color: getMutedText(darkMode),
                  }}
                >
                  No Retailers Found
                </Typography>
              )}
              {mostRecentDate ? (
                <Box paddingRight="5px">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: "12px",
                      color: getMutedText(darkMode),
                    }}
                  >
                    {`Last updated: ${momenttz(mostRecentDate)
                      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                      .format("ddd MMM DD YYYY @ hh:mm a zz")}`}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              <Link
                underline="hover"
                href={packwizardUrl}
                sx={{
                  padding: "5px",
                  fontFamily: HEADING_FONT,
                  fontWeight: 600,
                  fontSize: "14px",
                  color: getAccent(darkMode),
                }}
              >
                View Price History
              </Link>
            </Stack>
          </Stack>
        </Container>
      )}
    </Paper>
  );
};

export default ItemRetailerModule;
